import type { Coding, Condition, Procedure } from "@medplum/fhirtypes";

import {
  ProceduresPickerCategoryCodes,
  ProceduresPickerSystems,
} from "@/encounters/constants/procedures";

import { toCarriedProblemListCategories } from "./diagnosis-problem-list";

/** The two flags that decide what a resource is categorised as, whoever is signed in. */
export type ProcedureCategoryContext = {
  isBiller: boolean;
  isNonClinical: boolean;
};

/**
 * What marks a resource as the biller section's rather than the encounter's clinical record.
 *
 * Independent of who entered it: `billing-code-author` already says whether a biller was at the
 * keyboard, and a biller working in the procedures tab is still recording clinical work.
 */
export const nonClinicalDataCoding = {
  system: ProceduresPickerSystems.NonClinicalData,
  code: ProceduresPickerCategoryCodes.NonClinicalData,
};

export const activeClinicalStatus: Condition["clinicalStatus"] = {
  coding: [{ system: ProceduresPickerSystems.ConditionClinicalStatus, code: "active" }],
};

export const buildConditionCategories = ({
  isBiller,
  isNonClinical,
  existing,
}: ProcedureCategoryContext & {
  existing?: Condition;
}): Condition["category"] => [
  {
    coding: [
      {
        system: ProceduresPickerSystems.ConditionCategory,
        code: ProceduresPickerCategoryCodes.EncounterDiagnosis,
      },
    ],
  },
  {
    coding: [
      {
        system: ProceduresPickerSystems.EncounterCodeOrigin,
        code: ProceduresPickerCategoryCodes.EncounterCreated,
      },
    ],
  },
  {
    coding: [
      {
        system: ProceduresPickerSystems.BillingCodeAuthor,
        code: isBiller
          ? ProceduresPickerCategoryCodes.Billing
          : ProceduresPickerCategoryCodes.Clinical,
      },
    ],
  },
  // Condition.category repeats, so this is its own entry alongside the others.
  ...(isNonClinical ? [{ coding: [nonClinicalDataCoding] }] : []),
  ...toCarriedProblemListCategories(existing),
];

export const buildProcedureCategory = ({
  isBiller,
  isNonClinical,
}: ProcedureCategoryContext): Procedure["category"] => ({
  // Procedure.category is a single CodeableConcept, so the marking joins the codings already there
  // rather than being added as a second category.
  coding: [
    {
      system: ProceduresPickerSystems.EncounterCodeOrigin,
      code: ProceduresPickerCategoryCodes.EncounterCreated,
    },
    {
      system: ProceduresPickerSystems.BillingCodeAuthor,
      code: isBiller
        ? ProceduresPickerCategoryCodes.Billing
        : ProceduresPickerCategoryCodes.Clinical,
    },
    ...(isNonClinical ? [nonClinicalDataCoding] : []),
  ],
});

const hasNonClinicalCategoryCoding = (codings: Coding[] | undefined): boolean =>
  codings?.some(
    (coding) =>
      coding.system === nonClinicalDataCoding.system && coding.code === nonClinicalDataCoding.code,
  ) ?? false;

/** Chart-level Procedure or Condition stamped with the non-clinical billing marker. */
export function isChartLevelNonClinicalProcedure(procedure: Procedure): boolean {
  if (procedure.encounter) return false;
  return hasNonClinicalCategoryCoding(procedure.category?.coding);
}

/** Chart-level Condition stamped with the non-clinical billing marker. */
export function isChartLevelNonClinicalCondition(condition: Condition): boolean {
  if (condition.encounter) return false;
  return (condition.category ?? []).some((category) =>
    hasNonClinicalCategoryCoding(category.coding),
  );
}
